/**
 * The signed-in screen: header, organisation scope, and the monitor list.
 *
 * ## Where the data comes from
 *
 * `client.listMonitors(...)` and nowhere else. There is no fixture, no seeded
 * array and no mock in this file or anything it imports — the list you see is
 * `GET /monitors` off the live API, and when the API is unreachable this screen
 * says so rather than falling back to something that looks like data.
 *
 * ## Four states, and the one that matters
 *
 * Loading, loaded, empty and failed are four different answers and this screen
 * gives four different screens. The one worth stating out loud is **empty**: an
 * organisation with no monitors gets a plain statement that nothing is
 * configured, not a spinner that never resolves and not a green all-clear.
 * `MonitorList.total` is what separates "this organisation has none" from "the
 * array came back empty", and rendering absence as health is the single failure
 * this whole engagement exists to prevent (§1, ACC-2).
 *
 * ## No polling
 *
 * The list is fetched on load, on an explicit refresh, and when the
 * organisation scope changes. It is never put on a timer. `GET /monitors` is an
 * authenticated request and slides the server's idle deadline exactly like any
 * other, so polling it would hold a session open to its absolute ceiling and
 * quietly undo REQ-701's idle expiry — the client would have removed a security
 * property the API paid for. `SessionClock` tracks both deadlines locally from
 * what login already told us, so nothing has to ask.
 */
import { useCallback, useEffect, useState } from "react";

import {
  HalyardAuthenticationError,
  describeSessionLifetime,
} from "../api/index.ts";
import type {
  AuthenticatedRequest,
  HalyardClient,
  MembershipBody,
  MonitorListBody,
  SessionBody,
  SessionClock,
  SessionCredential,
} from "../api/index.ts";
import { MonitorList } from "./MonitorList.tsx";
import { ProblemNotice } from "./ProblemNotice.tsx";

export interface MonitorsScreenProps {
  readonly client: HalyardClient;
  readonly credential: SessionCredential;
  readonly session: SessionBody;
  readonly clock: SessionClock;
  /** The scope this screen reads within, or null when the person has yet to pick one. */
  readonly organisationId: string | null;
  readonly onChooseOrganisation: (id: string) => void;
  readonly onSignOut: () => void;
  /** Called when the server says the credential is gone. Sends the person to sign in. */
  readonly onSessionExpired: () => void;
  /** Already run through `constrainAccent` — see `theme/state-colours.ts`. */
  readonly accent: string;
}

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "loaded"; readonly list: MonitorListBody }
  | { readonly kind: "failed"; readonly error: unknown };

function OrganisationPicker({
  memberships,
  selected,
  onChoose,
}: {
  readonly memberships: readonly MembershipBody[];
  readonly selected: string | null;
  readonly onChoose: (id: string) => void;
}): React.JSX.Element {
  return (
    <label className="field field--inline">
      <span className="field__label">Organisation</span>
      <select
        className="field__input"
        value={selected ?? ""}
        onChange={(event) => {
          onChoose(event.target.value);
        }}
      >
        {selected === null ? <option value="">Choose an organisation…</option> : null}
        {memberships.map((membership) => (
          <option key={membership.organisation_id} value={membership.organisation_id}>
            {/* The contract carries no organisation name or slug — `Membership` is
                an id and a role. The id is shown because it is what there is; a
                friendlier label would have to be invented. */}
            {membership.organisation_id} ({membership.role})
          </option>
        ))}
      </select>
    </label>
  );
}

export function MonitorsScreen({
  client,
  credential,
  session,
  clock,
  organisationId,
  onChooseOrganisation,
  onSignOut,
  onSessionExpired,
  accent,
}: MonitorsScreenProps): React.JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [reloadNonce, setReloadNonce] = useState(0);

  const refresh = useCallback(() => {
    setReloadNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    // Nothing to fetch until a scope exists. The picker is on screen; this is
    // not a loading state and must not render as one.
    if (organisationId === null && session.memberships.length > 1) return;

    const controller = new AbortController();
    let live = true;
    setLoad({ kind: "loading" });

    const request: AuthenticatedRequest = {
      credential,
      signal: controller.signal,
      clock,
      ...(organisationId === null ? {} : { organisationId }),
    };

    client
      .listMonitors(request)
      .then((list) => {
        if (live) setLoad({ kind: "loaded", list });
      })
      .catch((error: unknown) => {
        if (!live) return;
        if (controller.signal.aborted) return;
        // A 401 is not something to render on this screen — the credential is
        // gone and the only thing on offer is signing in again.
        if (error instanceof HalyardAuthenticationError) {
          onSessionExpired();
          return;
        }
        setLoad({ kind: "failed", error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, credential, clock, organisationId, session.memberships.length, reloadNonce, onSessionExpired]);

  // Read from the local mirror of the server's two clocks. No request goes out
  // to answer this — see the note at the top of the file.
  const lifetime = describeSessionLifetime(
    {
      idle_expires_at: clock.idleExpiresAt().toISOString(),
      absolute_expires_at: clock.absoluteExpiresAt.toISOString(),
    },
    {},
  );

  return (
    <div className="app">
      <header className="header" style={{ borderTopColor: accent }}>
        <div className="header__brand">
          <span className="header__mark" style={{ backgroundColor: accent }} aria-hidden="true" />
          <span className="header__name">Halyard Monitor</span>
        </div>

        <div className="header__scope">
          {session.memberships.length > 1 ? (
            <OrganisationPicker
              memberships={session.memberships}
              selected={organisationId}
              onChoose={onChooseOrganisation}
            />
          ) : (
            <span className="header__org">
              {session.organisation === null
                ? "No organisation"
                : `${session.organisation.id} (${session.organisation.role})`}
            </span>
          )}
        </div>

        <div className="header__actions">
          <span className="header__email">{session.email}</span>
          <button type="button" className="button" onClick={refresh}>
            Refresh
          </button>
          <button type="button" className="button" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>

      {lifetime.absoluteExpiryImminent ? (
        <div className="notice notice--warn" role="status">
          <p className="notice__headline">This session is about to end.</p>
          <p className="notice__body">
            Sessions have a hard ceiling that activity does not extend, so carrying on working will
            not push it back. It ends at {lifetime.absoluteExpiresAt.toLocaleString()}.
          </p>
        </div>
      ) : null}

      <main className="main">
        {organisationId === null && session.memberships.length > 1 ? (
          <div className="empty">
            <h2 className="empty__title">Choose an organisation</h2>
            <p className="empty__body">
              This account is a member of {session.memberships.length} organisations. Monitors are
              scoped to one of them, and the API returns that organisation&rsquo;s monitors and no
              others.
            </p>
          </div>
        ) : load.kind === "loading" ? (
          <div className="loading" role="status">
            <span className="spinner" aria-hidden="true" />
            <p>Loading monitors from the API…</p>
          </div>
        ) : load.kind === "failed" ? (
          <ProblemNotice error={load.error}>
            <button type="button" className="button button--primary" onClick={refresh}>
              Try again
            </button>
          </ProblemNotice>
        ) : load.list.total === 0 ? (
          <div className="empty">
            <h2 className="empty__title">No monitors are configured in this organisation.</h2>
            <p className="empty__body">
              The API returned a list of zero. That is a statement about configuration, not about
              health: nothing is being checked here, so nothing is known about anything.
            </p>
            <p className="empty__body">
              An empty estate is also why <code>GET /ready</code> answers 503 with{" "}
              <code>no_monitors_configured</code> rather than reporting the platform ready. That is
              correct behaviour and not a fault to chase.
            </p>
          </div>
        ) : load.list.monitors.length === 0 ? (
          <div className="empty">
            <h2 className="empty__title">The API reports {load.list.total} monitors but returned none.</h2>
            <p className="empty__body">
              <code>total</code> and the returned array disagree. This client is showing you that
              rather than reconciling it, because a count with nothing behind it is a finding about
              the API and not something a dashboard should smooth over.
            </p>
          </div>
        ) : (
          <MonitorList monitors={load.list.monitors} total={load.list.total} />
        )}
      </main>
    </div>
  );
}
